// PresetsPanel — reusable systemPromptAppend snippets. Pick one and
// it'll be applied to the chat's next send (as systemPromptAppend on
// run.start). Active preset persists for the rest of the session;
// editing the preset later does NOT re-apply to in-flight chats.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import { api, type Preset, type PresetMeta } from "../../lib/rpc";
import { confirm } from "../../components/confirm";
import { LibraryPanel, LibraryRow, LibraryEmpty } from "./library-panel";

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64) || "preset";
}

interface PresetsPanelProps {
  open: boolean;
  onClose: () => void;
  notify: (msg: string) => void;
  activePresetName: string | null;
  onApply: (preset: Preset) => void;
  onClear: () => void;
}

export function PresetsPanel({
  open, onClose, notify, activePresetName, onApply, onClear,
}: PresetsPanelProps) {
  const [list, setList] = useState<PresetMeta[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [draft, setDraft] = useState<Preset | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try { setList(await api.presetsList()); }
    catch (e: any) { notify(e?.message || "failed to load presets"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) refresh(); }, [open]);

  useEffect(() => {
    if (!active) { setDraft(null); return; }
    let cancelled = false;
    api.presetGet(active).then((p) => { if (!cancelled) setDraft(p); }).catch(() => {});
    return () => { cancelled = true; };
  }, [active]);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await api.presetSave({
        name: draft.name,
        description: draft.description,
        body: draft.body,
      });
      await refresh();
      // If we just saved the currently-active preset, push the fresh
      // body into the active-preset state. Without this, the next
      // send would still use the cached (often empty) body the user
      // applied before editing — which is what made "you are a pirate"
      // appear to do nothing.
      if (activePresetName === draft.name) onApply(draft);
      notify("Preset saved");
      setActive(draft.name);
      setCreating(false);
    } catch (e: any) { notify(e?.message || "save failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!draft) return;
    const ok = await confirm({
      title: `Delete preset "${draft.name}"?`,
      message: "This preset will be removed. If it's currently active, it'll be cleared.",
      confirmLabel: "Delete preset",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.presetDelete(draft.name);
      notify("Deleted");
      if (activePresetName === draft.name) onClear();
      await refresh();
      setDraft(null);
      setActive(null);
    } catch (e: any) { notify(e?.message || "delete failed"); }
  };

  const apply = () => {
    if (!draft) return;
    onApply(draft);
    notify(`Applied preset · ${draft.name}`);
    onClose();
  };

  const startNew = () => {
    setCreating(true);
    setActive(null);
    setDraft({ name: "", description: "", body: "" });
  };

  return (
    <LibraryPanel
      open={open}
      onClose={onClose}
      title="Prompt presets"
      icon="edit"
      sub={
        activePresetName
          ? `active · ${activePresetName}`
          : `${list.length} saved · pick one to apply to the next turn`
      }
      toolbar={
        <button className="icon-btn" onClick={startNew} title="New preset" type="button">
          <Icon name="plus" />
        </button>
      }
      list={
        loading
          ? <p className="lib-loading">loading…</p>
          : list.length === 0 && !creating
            ? <p className="lib-loading">No presets yet. Tap + to add one.</p>
            : list.map((p) => (
                <LibraryRow
                  key={p.name}
                  active={active === p.name}
                  name={p.name}
                  description={p.description}
                  badge={activePresetName === p.name ? "active" : undefined}
                  onClick={() => { setActive(p.name); setCreating(false); }}
                />
              ))
      }
      detail={
        !draft
          ? <LibraryEmpty icon="edit" headline="Prompt preset" sub="Pick one to view + apply, or tap + to add one." />
          : (
            <div className="lib-detail-pane">
              <div className="lib-detail-form">
                <div className="lib-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={draft.name}
                    disabled={!creating}
                    onInput={(e) => setDraft({ ...draft, name: slug((e.currentTarget as HTMLInputElement).value) })}
                    placeholder="my-preset"
                  />
                </div>
                <div className="lib-field">
                  <label>Description</label>
                  <input
                    type="text"
                    value={draft.description}
                    onInput={(e) => setDraft({ ...draft, description: (e.currentTarget as HTMLInputElement).value })}
                    placeholder="one-line hook"
                    maxLength={200}
                  />
                </div>
                <div className="lib-field">
                  <label>Body — appended to the system prompt on apply</label>
                  <textarea
                    rows={10}
                    value={draft.body}
                    onInput={(e) => setDraft({ ...draft, body: (e.currentTarget as HTMLTextAreaElement).value })}
                    placeholder="extra system-prompt content…"
                  />
                </div>
                <div className="lib-detail-actions">
                  {!creating && (
                    <button className="btn-ghost" type="button" onClick={remove}>Delete</button>
                  )}
                  {!creating && activePresetName === draft.name ? (
                    <button className="btn" type="button" onClick={() => { onClear(); notify("Preset cleared"); }}>
                      Clear
                    </button>
                  ) : !creating ? (
                    <button className="btn" type="button" onClick={apply}>
                      Apply to chat
                    </button>
                  ) : null}
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={save}
                    disabled={saving || !draft.name || !draft.description}
                  >
                    <Icon name="check" size={13} />
                    <span>{saving ? "Saving…" : "Save"}</span>
                  </button>
                </div>
              </div>
            </div>
          )
      }
    />
  );
}
